import {
	Checklist,
	ProcedureNodeStep,
} from "@/features/procedures/model/types/checklist";
import { CheckpointType } from "@/features/procedures/model/types/manualChecklist";

export const calculateIsStepActive = (
	step: ProcedureNodeStep,
	checklist: Checklist | undefined,
) => {
	if (!checklist || !step || step.CheckpointType === CheckpointType.Info)
		return false;

	const allSteps = checklist.Steps;
	if (!allSteps.some((x) => x.Id == step.Id)) {
		return false;
	}

	const stepIndex = allSteps.findIndex((x) => x.Id === step.Id);

	if (stepIndex === -1) {
		return false;
	}

	if (stepIndex === 0) {
		return true;
	}

	for (let i = stepIndex - 1; i >= 0; i--) {
		if (
			allSteps[i].IsSkipped ||
			allSteps[i].IsOverridden ||
			allSteps[i].CheckpointType === CheckpointType.Info
		) {
			if (i == 0) {
				return true;
			}

			continue;
		}

		return allSteps[i].Status === 1;
	}

	return true;
};

export const isMultipleFieldStep = (numberOfFields: number) => {
	return numberOfFields > 0;
};

export const isHeader = (step: ProcedureNodeStep) => {
	return step.Index === 1 && isMultipleFieldStep(step.NumberOfFields);
};

export const getRowTypeClass = (step: ProcedureNodeStep) => {
	const ribbon = step.IsSkipped
		? "ribbon ribbonGrey"
		: step.IsOverridden
			? "ribbon ribbonOrange"
			: "";
	return step?.ChecklistType === "Info"
		? "grid-row grid-row-heading grid-row-heading-info"
		: `grid-row grid-row-item ${ribbon}`;
};

export const getColumnTypeClass = (step: ProcedureNodeStep) => {
	return step?.ChecklistType === "Info"
		? "grid-column grid-column--info"
		: step.NumberOfFields === 0
			? "grid-column grid-column--chapter"
			: "grid-column grid-column--imported";
};

export const validSchemeId = (schemeId: number | undefined) => {
	return schemeId !== undefined && !isNaN(schemeId);
};

/** Count checkable steps (excludes headers and Info) and how many are completed. */
export const getChecklistProgress = (
	steps: ProcedureNodeStep[] | undefined,
) => {
	if (!steps?.length) return { completed: 0, total: 0 };
	const checkable = steps.filter(
		(s) => !isHeader(s) && s.CheckpointType !== CheckpointType.Info,
	);
	const completed = checkable.filter((s) => s.Status === 1).length;
	return { completed, total: checkable.length };
};

export function stringToColor(string: string) {
	let hash = 0;
	let i;

	for (i = 0; i < string.length; i += 1) {
		hash = string.charCodeAt(i) + ((hash << 5) - hash);
	}
	let color = "#";
	for (i = 0; i < 3; i += 1) {
		const value = (hash >> (i * 8)) & 0xff;
		color += `00${value.toString(16)}`.slice(-2);
	}

	return color;
}

export function getTextColorForBackground(hexColor: string): string {
	// Remove the hash at the start if it's there
	hexColor = hexColor.replace(/^#/, "");

	// Parse the r, g, b values
	const r = parseInt(hexColor.substring(0, 2), 16);
	const g = parseInt(hexColor.substring(2, 4), 16);
	const b = parseInt(hexColor.substring(4, 6), 16);

	// Calculate the luminance
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

	// Return black for light backgrounds and white for dark backgrounds
	return luminance > 0.5 ? "#000000" : "#FFFFFF";
}

export function stringAvatar(name: string | undefined) {
	if (!name || name === "Anonymous") {
		return {
			sx: {
				bgcolor: stringToColor("AA"),
				color: getTextColorForBackground(stringToColor("AA")),
			},
			children: "AA",
		};
	}
	const initials = name.split(" ");
	let children = initials[0][0]; // Always take the first character of the first word

	// Check if there is a second word and if so, add its first character
	if (initials.length > 1 && initials[1]) {
		children += initials[1][0];
	}

	return {
		sx: {
			bgcolor: stringToColor(name),
			color: getTextColorForBackground(stringToColor(name)),
		},
		children: children,
	};
}

/**
 * Transforms a color from backend (designed for light theme) to work in dark mode
 * If the color is light and we're in dark mode, it darkens it appropriately
 * @param hexColor - Hex color string (with or without #)
 * @param isDarkMode - Whether we're in dark mode
 * @returns Transformed hex color string
 */
export function transformColorForTheme(
	hexColor: string,
	isDarkMode: boolean,
): string {
	if (!hexColor || !isDarkMode) {
		return hexColor;
	}

	// Remove the hash if present
	const cleanHex = hexColor.replace(/^#/, "");

	// Handle short hex codes (e.g., #fff -> #ffffff)
	const fullHex =
		cleanHex.length === 3
			? cleanHex
					.split("")
					.map((char) => char + char)
					.join("")
			: cleanHex;

	if (fullHex.length !== 6) {
		return hexColor; // Invalid hex, return as-is
	}

	// Parse RGB values
	const r = parseInt(fullHex.substring(0, 2), 16);
	const g = parseInt(fullHex.substring(2, 4), 16);
	const b = parseInt(fullHex.substring(4, 6), 16);

	// Calculate relative luminance (0-1)
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

	// If color is light (luminance > 0.6), darken it for dark mode
	if (luminance > 0.6) {
		// Darken by reducing brightness while preserving hue
		// Scale down to a darker range suitable for dark mode
		const targetLuminance = 0.1; // Target luminance for dark mode backgrounds

		// Calculate how much to scale down
		const scale = targetLuminance / luminance;

		// Apply darkening with some saturation preservation
		const newR = Math.max(0, Math.min(255, Math.round(r * scale)));
		const newG = Math.max(0, Math.min(255, Math.round(g * scale)));
		const newB = Math.max(0, Math.min(255, Math.round(b * scale)));

		// Convert back to hex
		const toHex = (n: number) => {
			const hex = n.toString(16);
			return hex.length === 1 ? "0" + hex : hex;
		};

		return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
	}

	// If color is already dark or medium, return as-is
	return hexColor.startsWith("#") ? hexColor : `#${hexColor}`;
}

// Re-export getStepType (lives in checklistStepType.tsx because it contains JSX)
export { getStepType } from "./checklistStepType";
